import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../api";
import "./Tests.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

// SOC 2 Type 2 is about controls operating effectively *over a period*. We let the org owner pick how
// often the full audit re-runs, and we map that to the backend cron the scheduler already honours.
const FREQ = {
  daily:    { cron: "0 0 2 * * *" },
  weekly:   { cron: "0 0 2 * * MON" },
  biweekly: { cron: "0 0 2 1,15 * *" },
};
const DEFAULT_FREQ = "weekly";

// Cooldown countdown for the run-all button: "12:05" from a minute up, "45s" below that.
// Locale-independent on purpose — it sits inside an already-translated sentence.
function formatCountdown(totalSeconds) {
  const s = Math.max(0, Math.ceil(totalSeconds));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function cronToFreq(cron) {
  if (!cron) return null;
  const c = cron.trim();
  for (const k of Object.keys(FREQ)) if (FREQ[k].cron === c) return k;
  return "custom";
}

const STATUS_STYLE = {
  pass:     { color: "#16a34a", bg: "#dcfce7", icon: "fa-check-circle"  },
  fail:     { color: "#dc2626", bg: "#fee2e2", icon: "fa-times-circle"  },
  "no run": { color: "#64748b", bg: "#f1f5f9", icon: "fa-minus-circle" },
  // Not applicable / not run (e.g. vendor not connected) — distinct grey "N/A"; NOT a pass and NOT
  // a gap. Excluded from the compliance-score numerator and denominator on the backend.
  skipped:  { color: "#64748b", bg: "#eef2f6", icon: "fa-ban" },
  // Manual controls that haven't been evidenced yet — distinct from an automated
  // test that simply hasn't run, so it doesn't read as a failure/gap.
  "needs evidence": { color: "#b45309", bg: "#fef3c7", icon: "fa-clipboard-list" },
  // A run (scheduled or manual) is currently executing for this org — every row reads InProgress
  // until the run completes.
  "in progress": { color: "#2563eb", bg: "#dbeafe", icon: "fa-spinner fa-spin" },
};

// One test typically validates MANY assets (e.g. an MFA test covers every identity). We infer the
// vendor + asset class a test targets — mirroring the backend's scoping — and count the org's
// matching assets to portray coverage. Microsoft 365 identity assets live under the Entra/Azure provider.
const VENDOR_LABEL = { AWS: "AWS", AZURE: "Azure / O365", GITHUB: "GitHub", GITLAB: "GitLab", OKTA: "Okta", GCP: "GCP" };

function inferVendor(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("microsoft 365") || n.includes("m365") || n.includes("azure") || n.includes("entra")) return "AZURE";
  if (n.includes("aws")) return "AWS";
  if (n.includes("github")) return "GITHUB";
  if (n.includes("gitlab")) return "GITLAB";
  if (n.includes("okta")) return "OKTA";
  if (n.includes("gcp") || n.includes("google")) return "GCP";
  return null;
}
function inferClass(name) {
  const n = (name || "").toLowerCase();
  if (/mfa|user|admin|\brole|rbac|conditional access|guest|sign-in|password|credential|service principal|deprovision|inactive|directory|access key|security default|legacy auth|\biam/.test(n)) return "IDENTITY";
  if (/repo|branch|pull request|ci\/cd|code review/.test(n)) return "CODE";
  if (/storage|bucket|\bs3|\brds|blob|database|at rest|snowflake/.test(n)) return "DATABASE";
  if (/nsg|network|flow log|load balancer|security group|\bvpc|firewall|segregation/.test(n)) return "NETWORK";
  if (/\bvm |compute|container|\bacr|\becr|defender|guardduty|clock|vulnerab|instance|disk/.test(n)) return "COMPUTE";
  return null;
}

// Readable, pluralized label for a discovered-asset type coming from a test's real findings
// (backend assetCoverageType), e.g. "VPC" -> "VPCs", "SECURITY_GROUP" -> "security groups".
function assetTypeLabel(type, count) {
  const map = {
    VPC: "VPC", SECURITY_GROUP: "security group", EC2_INSTANCE: "EC2 instance",
    IAM_USER: "IAM user", IAM_GROUP: "IAM group", IAM_ROLE: "IAM role",
    S3_BUCKET: "S3 bucket", RDS_INSTANCE: "RDS instance", LOAD_BALANCER: "load balancer",
    AWS_KMS_KEY: "KMS key", AWS_SECRET: "secret", LAMBDA: "Lambda function",
    AWS_ASSET: "asset", ASSET: "asset",
  };
  const base = map[type] || (type || "asset").toLowerCase().replace(/_/g, " ");
  return count === 1 ? base : base + "s";
}

function Tests() {
  const { t } = useTranslation();
  const FREQ_LABEL = { daily: t("Tests.freqDaily"), weekly: t("Tests.freqWeekly"), biweekly: t("Tests.freqBiweekly") };
  const FREQ_BLURB = { daily: t("Tests.freqDailyBlurb"), weekly: t("Tests.freqWeeklyBlurb"), biweekly: t("Tests.freqBiweeklyBlurb") };
  const CLASS_LABEL = {
    IDENTITY: t("Tests.classIdentities"),
    COMPUTE: t("Tests.classCompute"),
    DATABASE: t("Tests.classDatabase"),
    NETWORK: t("Tests.classNetwork"),
    CODE: t("Tests.classCode"),
  };
  const navigate = useNavigate();
  const [testData, setTestData]               = useState([]);
  const [availableUsers, setAvailableUsers]   = useState([]);
  const [frameworks, setFrameworks]           = useState([]);
  const [selectedFramework, setSelectedFramework] = useState("");
  // Pre-apply a status filter when arriving from a dashboard link (e.g. "Failing tests"
  // / "Action items" → /dashboard/tests?status=FAIL) so the list isn't an unfiltered wall.
  const location = useLocation();
  const [selectedStatus, setSelectedStatus]   = useState(
    () => new URLSearchParams(location.search).get("status") || "");
  const [selectedSource, setSelectedSource]   = useState("");   // "" | PLATFORM | SIDECAR
  const [selectedTsc, setSelectedTsc]         = useState("");   // criteria chip: LIKE pattern or exact shorthand
  const [searchName, setSearchName]           = useState("");
  const [openDropdown, setOpenDropdown]       = useState(null);   // test.id
  const [dropdownPos, setDropdownPos]         = useState(null);   // { top/bottom, left }
  const [expandedRow, setExpandedRow]         = useState(null);   // idx
  const [currentPage, setCurrentPage]         = useState(1);
  const [testsPerPage, setTestsPerPage]       = useState(10);
  const [totalElements, setTotalElements]     = useState(0);
  const [stats, setStats]                     = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [toast, setToast]                     = useState(null);
  const [runningAll, setRunningAll]           = useState(false);
  const [runProgress, setRunProgress]         = useState(null); // { running, total, completed }
  // Suite runs are rate-limited server-side: one per org at a time, then a cooldown before the
  // next. We mirror that here so the button greys itself out instead of firing a request that
  // comes back 409/429 — the server guard is still the one that enforces it.
  const [runBlockedUntil, setRunBlockedUntil] = useState(0);   // epoch ms, 0 = not blocked
  const [nowMs, setNowMs]                     = useState(() => Date.now());
  const [refreshTick, setRefreshTick]         = useState(0);
  const runPollRef = useRef(null);
  const [mcpHidden, setMcpHidden]             = useState(() => {
    try { return localStorage.getItem("cataam_mcp_banner_dismissed") === "1"; } catch { return false; }
  });
  const dismissMcp = () => {
    setMcpHidden(true);
    try { localStorage.setItem("cataam_mcp_banner_dismissed", "1"); } catch {}
  };
  const [coverage, setCoverage]               = useState({ byKey: {}, total: 0, coveredAssets: 0, vendors: 0 });
  const [testFw, setTestFw]                   = useState({});   // testId -> [{ name, auditId }]
  const [trend, setTrend]                     = useState(null);   // { timeline, recovery, days }
  const [trendLoaded, setTrendLoaded]         = useState(false);
  const [freq, setFreq]                        = useState(null);   // current schedule key, "custom", or null
  const [customCron, setCustomCron]            = useState("");
  const [savingFreq, setSavingFreq]            = useState(false);
  const dropdownRef = useRef(null);

  // Pass/fail-over-time + recovery (MTTR) — the operating-effectiveness evidence for SOC 2 Type 2.
  useEffect(() => {
    axiosInstance.get("/audit/test-trend", { params: { days: 90 } })
      .then(res => setTrend(res.data))
      .catch(() => {})
      .finally(() => setTrendLoaded(true));
  }, []);

  // Current audit schedule (org-level cron the backend scheduler already runs).
  useEffect(() => {
    axiosInstance.get("/organization-settings")
      .then(res => {
        const arr = Array.isArray(res.data) ? res.data : [res.data];
        const withCron = arr.find(s => s && s.schedulingCronExpression);
        const cron = withCron ? withCron.schedulingCronExpression : null;
        setCustomCron(cron || "");
        setFreq(cronToFreq(cron) || DEFAULT_FREQ);
      })
      .catch(() => setFreq(DEFAULT_FREQ));
  }, []);

  const applyFrequency = async (key) => {
    const cron = FREQ[key] ? FREQ[key].cron : null;
    if (!cron) return;
    setSavingFreq(true);
    const prev = freq;
    setFreq(key);
    try {
      await axiosInstance.post("/organization-settings/add", { schedulingCronExpression: cron });
      setCustomCron(cron);
      setToast({ ok: true, msg: t("Tests.toastScheduleSaved", { freq: FREQ_LABEL[key].toLowerCase(), blurb: FREQ_BLURB[key].toLowerCase() }) });
    } catch {
      setFreq(prev);
      setToast({ ok: false, msg: t("Tests.toastScheduleError") });
    } finally {
      setSavingFreq(false);
    }
  };

  // Seconds left before another suite run is allowed. Drives the disabled button + countdown.
  const cooldownLeft = runBlockedUntil > nowMs ? Math.ceil((runBlockedUntil - nowMs) / 1000) : 0;

  // Tick once a second, but only while a cooldown is actually counting down — clearing the block
  // when it expires re-runs this effect and stops the timer, rather than re-rendering forever.
  useEffect(() => {
    if (runBlockedUntil <= Date.now()) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (now >= runBlockedUntil) setRunBlockedUntil(0);
    }, 1000);
    return () => clearInterval(id);
  }, [runBlockedUntil]);

  // The status endpoint reports the cooldown as "seconds from now"; convert once, on arrival, so
  // the countdown doesn't drift or restart every poll.
  const applyCooldown = useCallback((data) => {
    const secs = Number(data?.retryAfterSeconds) || 0;
    if (!data?.running && data?.blockedReason === "RUN_COOLDOWN" && secs > 0) {
      setRunBlockedUntil(Date.now() + secs * 1000);
      setNowMs(Date.now());
    } else if (data?.canRun) {
      setRunBlockedUntil(0);
    }
  }, []);

  // Poll run-all progress until the background suite finishes, refreshing the table as results land
  // so the user sees live "X of N complete" instead of guessing what's happening.
  const pollRunProgress = useCallback(() => {
    if (runPollRef.current) clearInterval(runPollRef.current);
    runPollRef.current = setInterval(async () => {
      try {
        const { data } = await axiosInstance.get("/audit/run-all/status");
        setRunProgress(data);
        setRefreshTick(t => t + 1); // surface freshly-completed results in the table
        if (!data.running) {
          clearInterval(runPollRef.current);
          runPollRef.current = null;
          setRunningAll(false);
          applyCooldown(data); // the run just finished — start the countdown to the next one
          const done = data.total || 0;
          setToast({ ok: true, msg: t("Tests.toastSuiteComplete", { count: done }) });
          setTimeout(() => setRunProgress(null), 4000);
        }
      } catch {
        clearInterval(runPollRef.current);
        runPollRef.current = null;
        setRunningAll(false);
      }
    }, 3000);
  }, [t, applyCooldown]);

  // On-demand "run the whole suite" (apart from the schedule). Returns immediately; we then poll
  // progress and keep the button busy with a live count until the background run finishes.
  const runAllTests = async () => {
    if (runningAll || cooldownLeft > 0) return;   // belt and braces — the button is disabled too
    setRunningAll(true);
    try {
      const res = await axiosInstance.post("/audit/run-all");
      const d = res.data || {};
      setRunProgress({ running: true, total: d.automatedQueued ?? 0, completed: 0 });
      setToast({ ok: true, msg: t("Tests.toastSuiteStarted", { queued: d.automatedQueued ?? 0 }) });
      setRefreshTick(t => t + 1);
      pollRunProgress();
    } catch (err) {
      const status = err?.response?.status;
      const body   = err?.response?.data || {};
      const secs   = Number(body.retryAfterSeconds) || 0;
      if (status === 409) {
        // A run is already in flight — started in another tab, by a colleague, or on the other
        // API node. Attach to it and show its progress rather than reporting a failure.
        setRunProgress({ running: true, total: 0, completed: 0 });
        setToast({ ok: true, msg: t("Tests.toastSuiteAlreadyRunning") });
        pollRunProgress();
        return;   // deliberately leaves runningAll true
      }
      setRunningAll(false);
      if (status === 429) {
        if (secs > 0) { setRunBlockedUntil(Date.now() + secs * 1000); setNowMs(Date.now()); }
        setToast({ ok: false, msg: t("Tests.toastSuiteCooldown", { minutes: Math.max(1, Math.ceil(secs / 60)) }) });
      } else if (status === 503) {
        setToast({ ok: false, msg: t("Tests.toastSuiteBusy") });
      } else {
        setToast({ ok: false, msg: t("Tests.toastSuiteStartError") });
      }
    }
  };

  // If a suite run is already in progress (e.g. started elsewhere, or this page was reloaded),
  // pick it up and show progress rather than a fresh idle button. Also picks up a cooldown left
  // over from a run that finished while the user was on another page.
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/audit/run-all/status").then(({ data }) => {
      if (cancelled || !data) return;
      if (data.running) {
        setRunningAll(true);
        setRunProgress(data);
        pollRunProgress();
      } else {
        applyCooldown(data);
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (runPollRef.current) clearInterval(runPollRef.current);
    };
  }, [pollRunProgress, applyCooldown]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/audit/tests", {
          params: {
            page:      currentPage - 1,
            size:      testsPerPage,
            frameWork: selectedFramework || undefined,
            status:    selectedStatus    || undefined,
            source:    selectedSource    || undefined,
            category:  selectedTsc       || undefined,
            testName:  searchName.trim() || undefined,
          },
        });

        const payload = res.data[0] || {};
        const tests   = payload.auditProgressList || [];
        setTotalElements(payload.totalElements || 0);

        const s = payload.auditProgressTestsStatsResponseDTO;
        if (s) {
          setStats({
            total:     s.totalTests     || 0,
            automated: s.automatedTests || 0,
            manual:    s.manualTests    || 0,
            passed:    s.passedTests    || 0,
            failed:    s.failedTests    || 0,
          });
        }

        setTestData(tests.map(t => ({ ...t, assignedTo: t.testExecutioner || "" })));
      } catch {
        // load failed silently
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentPage, testsPerPage, selectedFramework, selectedStatus, selectedSource, selectedTsc, searchName, refreshTick]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [fwRes, usersRes] = await Promise.all([
          axiosInstance.get("/frameworks"),
          axiosInstance.get("/users"),
        ]);
        setFrameworks(fwRes.data || []);
        setAvailableUsers(usersRes.data || []);
      } catch {}
    };
    loadMeta();
  }, []);

  // Which framework(s)/audit(s) each test belongs to — a test commonly spans several audits, so we
  // surface that per row instead of leaving the user to guess. testId -> [{ name, auditId }].
  useEffect(() => {
    axiosInstance.get("/audit/test-frameworks")
      .then(res => {
        const map = {};
        (res.data || []).forEach(r => {
          const list = (map[r.testId] = map[r.testId] || []);
          if (!list.some(e => e.name === r.frameworkName)) {
            list.push({ name: r.frameworkName, auditId: r.auditId });
          }
        });
        setTestFw(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Build asset coverage: cross-tab the org's assets by (vendor, class), then mark which combos
  // have at least one test — so the page can show that one test spans many assets, and overall coverage.
  useEffect(() => {
    const buildCoverage = async () => {
      try {
        const [assetsRes, allTestsRes] = await Promise.all([
          axiosInstance.get("/iasm/assets", { params: { size: 500 } }),
          axiosInstance.get("/audit/tests", { params: { page: 0, size: 1000 } }),
        ]);
        const assets = assetsRes.data.content || [];
        const byKey = {}; // "VENDOR|CLASS" -> { count, names: [] }
        for (const a of assets) {
          const k = `${(a.cloudProvider || "").toUpperCase()}|${(a.layer || "").toUpperCase()}`;
          (byKey[k] = byKey[k] || { count: 0, names: [] }).count++;
          if (byKey[k].names.length < 8) byKey[k].names.push(a.displayName || a.name);
        }
        const allTests = (allTestsRes.data[0] || {}).auditProgressList || [];
        const coveredKeys = new Set();
        for (const t of allTests) {
          const v = inferVendor(t.testName), c = inferClass(t.testName);
          if (v && c) coveredKeys.add(`${v}|${c}`);
        }
        let coveredAssets = 0;
        const vendorsCovered = new Set();
        Object.keys(byKey).forEach(k => {
          if (coveredKeys.has(k)) { coveredAssets += byKey[k].count; vendorsCovered.add(k.split("|")[0]); }
        });
        setCoverage({ byKey, total: assets.length, coveredAssets, vendors: vendorsCovered.size });
      } catch {
        // coverage is best-effort; page still works without it
      }
    };
    buildCoverage();
  }, []);

  const coverageForTest = (test) => {
    // Prefer authoritative coverage from the test's real findings (how many assets it actually
    // evaluated, and of what type) — set by the backend from the latest result.
    if (test.assetCoverageCount > 0 && test.assetCoverageType) {
      return {
        count: test.assetCoverageCount,
        typeLabel: assetTypeLabel(test.assetCoverageType, test.assetCoverageCount),
        names: [],
        fromFindings: true,
      };
    }
    // Fallback: infer (vendor, class) from the test name against the iasm asset cross-tab.
    const v = inferVendor(test.testName), c = inferClass(test.testName);
    if (!v || !c) return null; // org/tenant-level or generic — not asset-scoped
    const hit = coverage.byKey[`${v}|${c}`];
    return { vendor: v, cls: c, count: hit ? hit.count : 0, names: hit ? hit.names : [] };
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setDropdownPos(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Open dropdown via portal — measure button rect to position with fixed coords
  const openOwnerDropdown = useCallback((e, testId) => {
    e.stopPropagation();
    if (openDropdown === testId) {
      setOpenDropdown(null);
      setDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 230;
    setDropdownPos(openUpward
      ? { bottom: window.innerHeight - rect.top + 2, left: rect.left }
      : { top: rect.bottom + 2, left: rect.left }
    );
    setOpenDropdown(testId);
  }, [openDropdown]);

  const assignOwner = async (test, userName, userId) => {
    setTestData(prev => prev.map(t =>
      t.id === test.id ? { ...t, assignedTo: userName } : t
    ));
    setOpenDropdown(null);
    setDropdownPos(null);

    if (!userId) {
      setToast({ ok: true, msg: t("Tests.toastOwnerCleared") });
      return;
    }
    try {
      await axiosInstance.post(`/tests/${test.testId}/assign-owner/${userId}`);
      setToast({ ok: true, msg: t("Tests.toastOwnerAssigned") });
    } catch {
      setToast({ ok: false, msg: t("Tests.toastOwnerLocal") });
    }
  };

  const handleDueDateChange = async (testId, newValue) => {
    try {
      await axiosInstance.put(`/audit/tests/${testId}/update-due-date`, null, {
        params: { newDueDate: newValue.toISOString() },
      });
      setTestData(prev => prev.map(t =>
        t.id === testId ? { ...t, dueDate: newValue.format("YYYY-MM-DDTHH:mm:ss") } : t
      ));
    } catch {}
  };

  const executeTest = async (e, test) => {
    e.stopPropagation();
    if (test.type !== "AUTOMATED" || runningAll) return;
    try {
      await axiosInstance.post(`/${test.id}/runtest`);
      setToast({ ok: true, msg: t("Tests.toastTestStarted") });
    } catch {
      setToast({ ok: false, msg: t("Tests.toastTestError") });
    }
  };

  // Public legal-document controls (privacy / cookie / terms) can be satisfied by providing the
  // live page URL — Cataam fetches and content-validates it. This is the fallback when Cataam's
  // ASM can't auto-discover the page, or the org doesn't run ASM.
  const isLegalDocTest = (name) => /privacy|cookie|terms/i.test(name || "");
  const [urlEntry, setUrlEntry] = useState(null); // { test, value, busy, error }

  const submitLegalDocUrl = async () => {
    if (!urlEntry || !urlEntry.value.trim()) return;
    setUrlEntry(u => ({ ...u, busy: true, error: null }));
    try {
      const res = await axiosInstance.post("/audit/legal-doc/validate", {
        testName: urlEntry.test.testName,
        url: urlEntry.value.trim(),
      });
      const { valid, note } = res.data || {};
      setToast({ ok: !!valid, msg: valid ? t("Tests.toastUrlValid", { note }) : t("Tests.toastUrlInvalid", { note }) });
      setUrlEntry(null);
      setRefreshTick(t => t + 1);
    } catch (err) {
      setUrlEntry(u => ({ ...u, busy: false, error: err?.response?.data?.error || t("Tests.urlValidationFailed") }));
    }
  };

  const statusKey = (s) => {
    if (!s) return "no run";
    const l = s.toString().toLowerCase();
    if (l === "pass") return "pass";
    if (l === "fail") return "fail";
    if (l === "skipped") return "skipped";
    return "no run";
  };

  const totalPages = Math.ceil(totalElements / testsPerPage) || 1;

  return (
    <div className="ts-page">
      {/* Cataam Claude Plugin / MCP awareness — drive compliance from Claude */}
      {!mcpHidden && (
        <div className="ts-mcp-banner">
          <i className="fas fa-wand-magic-sparkles ts-mcp-icon" />
          <div className="ts-mcp-text">
            <strong>{t("Tests.mcpHeading")}</strong> {t("Tests.mcpBody")}{" "}
            <code>npx cataam-mcp-server</code> {t("Tests.mcpWithApiKey")}
            <span className="ts-mcp-note"> {t("Tests.mcpNote")}</span>
          </div>
          <a className="ts-mcp-cta" href="https://github.com/themarkups/claude-plugin" target="_blank" rel="noreferrer">
            <i className="fab fa-github" /> {t("Tests.mcpGetPlugin")}
          </a>
          <button className="ts-mcp-dismiss" onClick={dismissMcp} title={t("Tests.dismiss")} aria-label={t("Tests.dismiss")}>
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* Stat bar */}
      {stats && (
        <div className="ts-stat-bar">
          {[
            { val: stats.total,     label: t("Tests.statTotal")     },
            { val: stats.automated, label: t("Tests.statAutomated") },
            { val: stats.manual,    label: t("Tests.statManual")    },
            { val: stats.passed,    label: t("Tests.statPassed"),   cls: "ts-val-ok"     },
            { val: stats.failed,    label: t("Tests.statFailed"),   cls: "ts-val-danger" },
          ].map(s => (
            <div key={s.label} className="ts-stat">
              <span className={`ts-stat-val ${s.cls || ""}`}>{s.val}</span>
              <span className="ts-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* One-test-many-assets callout + overall coverage */}
      {coverage.total > 0 && (
        <div className="ts-coverage-banner">
          <i className="fas fa-shield-alt ts-cov-icon" />
          <div className="ts-cov-text">
            <strong>{t("Tests.coverageHeading")}</strong> {t("Tests.coverageBody")}
            {coverage.byKey["AZURE|IDENTITY"]
              ? t("Tests.coverageAzure", { count: coverage.byKey["AZURE|IDENTITY"].count })
              : ""}
            {coverage.byKey["AWS|IDENTITY"] ? t("Tests.coverageAws", { count: coverage.byKey["AWS|IDENTITY"].count }) : ""}.
          </div>
          <div className="ts-cov-metric">
            <span className="ts-cov-num">{coverage.coveredAssets}<span className="ts-cov-den">/{coverage.total}</span></span>
            <span className="ts-cov-lbl">{t("Tests.coverageMetric", { count: coverage.vendors })}</span>
          </div>
        </div>
      )}

      {/* SOC 2 Type 2 strip: audit schedule · pass/fail over time · recovery (MTTR) */}
      <div className="ts-type2-grid">
        {/* Schedule picker */}
        <div className="ts-t2-card ts-sched-card">
          <div className="ts-t2-head">
            <i className="fas fa-clock ts-t2-icon" />
            <span className="ts-t2-title">{t("Tests.auditScheduleTitle")}</span>
          </div>
          <p className="ts-t2-sub">{t("Tests.auditScheduleSub")}</p>
          <div className="ts-freq-group" role="group" aria-label={t("Tests.auditFrequencyAria")}>
            {Object.keys(FREQ).map(k => (
              <button
                key={k}
                className={`ts-freq-btn ${freq === k ? "ts-freq-active" : ""}`}
                disabled={savingFreq}
                onClick={() => applyFrequency(k)}
                title={FREQ_BLURB[k]}
              >
                {FREQ_LABEL[k]}
              </button>
            ))}
          </div>
          <p className="ts-freq-note">
            {savingFreq ? (
              <><i className="fas fa-spinner fa-spin" /> {t("Tests.saving")}</>
            ) : freq === "custom" ? (
              <>{t("Tests.customSchedule")} <code>{customCron}</code></>
            ) : freq && FREQ_BLURB[freq] ? (
              <><i className="fas fa-check-circle" style={{ color: "#16a34a" }} /> {FREQ_BLURB[freq]}</>
            ) : "—"}
          </p>
        </div>

        {/* Pass/fail over time */}
        <div className="ts-t2-card ts-trend-card">
          <div className="ts-t2-head">
            <i className="fas fa-chart-area ts-t2-icon" />
            <span className="ts-t2-title">{t("Tests.passFailOverTime")}</span>
            <span className="ts-t2-range">{t("Tests.lastNDays", { count: (trend && trend.days) || 90 })}</span>
          </div>
          {trend && trend.timeline && trend.timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={trend.timeline} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="tsPass" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="tsFail" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }}
                  tickFormatter={d => dayjs(d).format("MMM D")} minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} width={28} />
                <Tooltip
                  labelFormatter={d => dayjs(d).format("MMM D, YYYY")}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Area type="monotone" dataKey="passed" name={t("Tests.chartPassed")} stroke="#16a34a" fill="url(#tsPass)" strokeWidth={2} />
                <Area type="monotone" dataKey="failed" name={t("Tests.chartFailed")} stroke="#dc2626" fill="url(#tsFail)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : !trendLoaded ? (
            <div className="ts-t2-empty">
              <i className="fas fa-spinner fa-spin" /> {t("Tests.loading")}
            </div>
          ) : (
            <div className="ts-t2-empty">
              <i className="fas fa-stream" /> {t("Tests.noRunHistory")}
            </div>
          )}
        </div>

        {/* Recovery / MTTR */}
        <div className="ts-t2-card ts-mttr-card">
          <div className="ts-t2-head">
            <i className="fas fa-heartbeat ts-t2-icon" />
            <span className="ts-t2-title">{t("Tests.timeToRecovery")}</span>
          </div>
          {trend && trend.recovery ? (
            <>
              <div className="ts-mttr-figures">
                <div className="ts-mttr-fig">
                  <span className="ts-mttr-num">
                    {trend.recovery.avgRecoveryHours > 0 ? trend.recovery.avgRecoveryHours : "—"}
                    {trend.recovery.avgRecoveryHours > 0 && <span className="ts-mttr-unit">{t("Tests.hoursUnit")}</span>}
                  </span>
                  <span className="ts-mttr-lbl">{t("Tests.avgMttr")}</span>
                </div>
                <div className="ts-mttr-fig">
                  <span className="ts-mttr-num ts-mttr-ok">{trend.recovery.recoveredCount}</span>
                  <span className="ts-mttr-lbl">{t("Tests.recovered")}</span>
                </div>
                <div className="ts-mttr-fig">
                  <span className={`ts-mttr-num ${trend.recovery.openFailures > 0 ? "ts-mttr-danger" : ""}`}>
                    {trend.recovery.openFailures}
                  </span>
                  <span className="ts-mttr-lbl">{t("Tests.open")}</span>
                </div>
              </div>
              <div className="ts-mttr-list">
                {(trend.recovery.items || []).slice(0, 4).map((it, i) => (
                  <div key={i} className="ts-mttr-item" title={it.testName}>
                    <i className={`fas ${it.open ? "fa-exclamation-circle" : "fa-check-circle"}`}
                      style={{ color: it.open ? "#dc2626" : "#16a34a" }} />
                    <span className="ts-mttr-item-name">{it.testName}</span>
                    <span className="ts-mttr-item-dur">
                      {it.open ? t("Tests.mttrItemOpen", { hours: it.hours }) : t("Tests.mttrItemHours", { hours: it.hours })}
                    </span>
                  </div>
                ))}
                {(!trend.recovery.items || trend.recovery.items.length === 0) && (
                  <div className="ts-t2-empty ts-t2-empty-sm">{t("Tests.noRecoveryWindows")}</div>
                )}
              </div>
            </>
          ) : !trendLoaded ? (
            <div className="ts-t2-empty"><i className="fas fa-spinner fa-spin" /> {t("Tests.loading")}</div>
          ) : (
            <div className="ts-t2-empty"><i className="fas fa-heartbeat" /> {t("Tests.awaitingRunHistory")}</div>
          )}
        </div>
      </div>

      {/* Table card */}
      <div className="ts-card">
        {/* Toolbar */}
        <div className="ts-toolbar">
          <div className="ts-search-wrap">
            <i className="fas fa-search ts-search-icon" />
            <input
              className="ts-search"
              placeholder={t("Tests.searchPlaceholder")}
              value={searchName}
              onChange={e => { setSearchName(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="ts-filter-wrap">
            <i className="fas fa-filter ts-filter-icon" />
            <select className="ts-select" value={selectedFramework}
              onChange={e => { setSelectedFramework(e.target.value); setCurrentPage(1); }}>
              <option value="">{t("Tests.allFrameworks")}</option>
              {frameworks.map(fw => (
                <option key={fw.id} value={fw.displayName}>{fw.displayName}</option>
              ))}
            </select>
          </div>
          <div className="ts-filter-wrap">
            <select className="ts-select" value={selectedStatus}
              onChange={e => { setSelectedStatus(e.target.value); setCurrentPage(1); }}>
              <option value="">{t("Tests.allStatuses")}</option>
              <option value="PASS">{t("Tests.statusPassed")}</option>
              <option value="FAIL">{t("Tests.statusFailed")}</option>
              <option value="SKIPPED">{t("Tests.statusSkipped")}</option>
              <option value="norun">{t("Tests.statusNoRun")}</option>
            </select>
          </div>
          <div className="ts-filter-wrap">
            <select className="ts-select" value={selectedSource}
              onChange={e => { setSelectedSource(e.target.value); setCurrentPage(1); }}>
              <option value="">{t("Tests.allSources")}</option>
              <option value="PLATFORM">{t("Tests.sourcePlatform")}</option>
              <option value="SIDECAR">{t("Tests.sourceAttackSurface")}</option>
            </select>
          </div>
          <div className="ts-tsc-chips">
            {[
              ['',     t('Tests.tscAll')],
              ['cc%',  'CC'],
              ['sd%',  'SD'],
              ['a1',   'A1'],
              ['c1',   'C1'],
              ['pi1',  'PI1'],
              ['p',    'P'],
            ].map(([val, label]) => (
              <button key={label} type="button"
                className={`ts-tsc-chip ${selectedTsc === val ? 'active' : ''}`}
                onClick={() => { setSelectedTsc(val); setCurrentPage(1); }}>
                {label}
              </button>
            ))}
          </div>
          {loading && (
            <span className="ts-loading-label">
              <i className="fas fa-spinner fa-spin" /> {t("Tests.loading")}
            </span>
          )}
          <button
            className="ts-run-all-btn"
            onClick={runAllTests}
            disabled={runningAll || cooldownLeft > 0}
            title={cooldownLeft > 0 ? t("Tests.runAllCooldownTitle") : t("Tests.runAllTitle")}
          >
            {runningAll
              ? (runProgress && runProgress.total > 0
                  ? <><i className="fas fa-spinner fa-spin" /> {t("Tests.runningProgress", { completed: runProgress.completed ?? 0, total: runProgress.total })}</>
                  : <><i className="fas fa-spinner fa-spin" /> {t("Tests.inProgress")}</>)
              : <><i className="fas fa-play" /> {t("Tests.runAllTests")}</>}
          </button>
          {!runningAll && cooldownLeft > 0 && (
            // Say when, not just "no". A user who has just fixed something can still re-run that
            // one test from its row — the per-test button is not rate-limited.
            <span className="ts-run-cooldown" title={t("Tests.runAllCooldownTitle")}>
              <i className="fas fa-clock" /> {t("Tests.runAllCooldownHint", { time: formatCountdown(cooldownLeft) })}
            </span>
          )}
          {runningAll && runProgress && runProgress.total > 0 && (
            <div className="ts-run-progress" title={t("Tests.suiteRunInProgress")}>
              <div
                className="ts-run-progress-bar"
                style={{ width: `${Math.min(100, Math.round(((runProgress.completed ?? 0) / runProgress.total) * 100))}%` }}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="ts-table-wrap">
          <table className="ts-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} />
                <th>{t("Tests.colTestName")}</th>
                <th>{t("Tests.colCategory")}</th>
                <th>{t("Tests.colStatus")}</th>
                <th>{t("Tests.colAudits")}</th>
                <th>{t("Tests.colType")}</th>
                <th>{t("Tests.colCoverage")}</th>
                <th>{t("Tests.colOwner")}</th>
                <th>{t("Tests.colDueDate")}</th>
                <th>{t("Tests.colJira")}</th>
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {testData.length === 0 ? (
                <tr>
                  <td colSpan={11} className="ts-empty">
                    {loading ? t("Tests.loadingTests") : t("Tests.noTestsFound")}
                  </td>
                </tr>
              ) : (
                testData.map((test, idx) => {
                  // Manual controls with no result yet show "Needs Evidence" rather than "No Run",
                  // so they read as awaiting human evidence — not as an un-run/failed automated test.
                  const needsEvidence = !test.lastAuditStatus && test.type && test.type !== "AUTOMATED";
                  // While a run is in progress for this org, every test reads InProgress until it completes.
                  const sk = runningAll ? "in progress" : (needsEvidence ? "needs evidence" : statusKey(test.lastAuditStatus));
                  const ss = STATUS_STYLE[sk];
                  const statusLabel = runningAll
                    ? t("Tests.inProgress")
                    : (test.lastAuditStatus
                      ? test.lastAuditStatus.toString()
                      : (needsEvidence ? t("Tests.needsEvidence") : t("Tests.noRun")));
                  const isExpanded = expandedRow === idx;
                  return (
                    <React.Fragment key={test.id}>
                      <tr
                        className={`ts-row ${isExpanded ? "ts-row-open" : ""}`}
                        onClick={() => setExpandedRow(isExpanded ? null : idx)}
                      >
                        {/* Chevron column */}
                        <td className="ts-chevron-cell">
                          <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} ts-row-chevron`} />
                        </td>
                        <td className="ts-test-name">{test.testName || "—"}</td>
                        <td>
                          <span className="ts-cat-badge">{test.testCategory || "—"}</span>
                        </td>
                        <td>
                          <span className="ts-status-badge" style={{ color: ss.color, background: ss.bg }}>
                            <i className={`fas ${ss.icon}`} />
                            {statusLabel}
                          </span>
                        </td>
                        {/* Audit(s) — which framework audits include this test (a test often spans several) */}
                        <td onClick={e => e.stopPropagation()}>
                          {(testFw[test.testId] && testFw[test.testId].length > 0) ? (
                            <span className="ts-fw-cell">
                              {testFw[test.testId].map(fw => (
                                <button
                                  key={fw.name}
                                  className="ts-fw-badge"
                                  title={t("Tests.fwBadgeTitle", { audit: fw.auditId || fw.name })}
                                  onClick={() => navigate("/dashboard/compliance/audits")}
                                >
                                  {fw.name}
                                </button>
                              ))}
                            </span>
                          ) : (
                            <span className="ts-cov-na">—</span>
                          )}
                        </td>
                        <td>
                          <span className={`ts-type-badge ${test.type === "AUTOMATED" ? "ts-type-auto" : "ts-type-manual"}`}>
                            {test.type || "—"}
                          </span>
                          {test.testCategory === "Attack Surface" && (
                            <span className="ts-continuous-badge" title={t("Tests.continuousTitle")}>
                              <i className="fas fa-bolt" /> {t("Tests.continuous")}
                            </span>
                          )}
                        </td>
                        {/* Coverage — how many assets this one test validates */}
                        <td onClick={e => e.stopPropagation()}>
                          {(() => {
                            const cov = coverageForTest(test);
                            if (!cov) return <span className="ts-cov-na" title={t("Tests.orgLevelTitle")}>{t("Tests.orgLevel")}</span>;
                            const covLabel = cov.fromFindings ? cov.typeLabel : (CLASS_LABEL[cov.cls] || t("Tests.assets"));
                            const covTitle = cov.fromFindings
                              ? `${cov.count} ${cov.typeLabel} evaluated in the latest run`
                              : `${VENDOR_LABEL[cov.vendor] || cov.vendor} · ${CLASS_LABEL[cov.cls] || cov.cls}${cov.names.length ? t("Tests.covPillExamples", { names: cov.names.slice(0, 4).join(", ") }) : ""}`;
                            return (
                              <span className="ts-cov-pill" title={covTitle}>
                                <i className="fas fa-layer-group" /> {cov.count} {covLabel}
                              </span>
                            );
                          })()}
                        </td>
                        {/* Owner — stopPropagation so row click doesn't trigger */}
                        <td className="ts-owner-cell" onClick={e => e.stopPropagation()}>
                          <button
                            className="ts-owner-btn"
                            onClick={e => openOwnerDropdown(e, test.id)}
                          >
                            {test.assignedTo || t("Tests.unassigned")}
                            <i className="fas fa-chevron-down" />
                          </button>
                        </td>
                        {/* Date — stopPropagation so row click doesn't trigger */}
                        <td className="ts-date-cell" onClick={e => e.stopPropagation()}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={test.dueDate ? dayjs(test.dueDate) : null}
                              onChange={v => handleDueDateChange(test.id, v)}
                              format="MM/DD/YYYY"
                              slotProps={{
                                textField: {
                                  variant: "standard",
                                  size: "small",
                                  placeholder: t("Tests.notSet"),
                                  InputProps: {
                                    disableUnderline: true,
                                    style: { fontSize: "0.8125rem", color: "#334155" },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </td>
                        {/* Jira ID — stopPropagation so row click doesn't trigger */}
                        <td onClick={e => e.stopPropagation()}>
                          {test.jiraId ? (
                            <a
                              href={`https://markups.atlassian.net/browse/${test.jiraId}`}
                              target="_blank" rel="noreferrer"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '2px 7px', borderRadius: '4px', fontWeight: 600,
                                background: '#e0f2fe', color: '#0369a1', textDecoration: 'none',
                                border: '1px solid #bae6fd', fontSize: '11px', whiteSpace: 'nowrap',
                              }}
                            >
                              <i className="fab fa-jira" style={{ fontSize: '10px' }} />
                              {test.jiraId}
                            </a>
                          ) : (
                            <span style={{ color: '#d1d5db', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        {/* Run button — stopPropagation so row click doesn't trigger */}
                        <td className="ts-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className={`ts-run-btn ${(test.type !== "AUTOMATED" || runningAll) ? "ts-run-disabled" : ""}`}
                            onClick={e => executeTest(e, test)}
                            disabled={test.type !== "AUTOMATED" || runningAll}
                            title={runningAll ? t("Tests.inProgress") : (test.type !== "AUTOMATED" ? t("Tests.runBtnManualTitle") : t("Tests.runBtnTitle"))}
                          >
                            {t("Tests.run")}
                          </button>
                          {isLegalDocTest(test.testName) && (
                            <button
                              className="ts-run-btn"
                              style={{ marginLeft: 6, background: "#eef2ff", color: "#4338ca", borderColor: "#c7d2fe" }}
                              onClick={() => setUrlEntry({ test, value: "", busy: false, error: null })}
                              title={t("Tests.provideUrlTitle")}
                            >
                              {t("Tests.provideUrl")}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Accordion detail row */}
                      {isExpanded && (
                        <tr className="ts-detail-row">
                          <td colSpan={11}>
                            <div className="ts-detail-grid">
                              <div className="ts-detail-block ts-desc-block">
                                <p className="ts-detail-label">{t("Tests.colTestName")}</p>
                                <p className="ts-detail-val">{test.testName || "—"}</p>
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.colCategory")}</p>
                                <p className="ts-detail-val">{test.testCategory || "—"}</p>
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.detailControl")}</p>
                                <p className="ts-detail-val ts-monospace">{test.controlId || "—"}</p>
                              </div>
                              <div className="ts-detail-block ts-desc-block">
                                <p className="ts-detail-label">{t("Tests.detailAssetCoverage")}</p>
                                {(() => {
                                  const cov = coverageForTest(test);
                                  if (!cov) return <p className="ts-detail-val">{t("Tests.detailOrgLevel")}</p>;
                                  return (
                                    <p className="ts-detail-val">
                                      {t("Tests.detailValidatesPre")} <strong>{cov.count}</strong> {cov.fromFindings ? cov.typeLabel : `${VENDOR_LABEL[cov.vendor] || cov.vendor} ${CLASS_LABEL[cov.cls] || t("Tests.assets")}`} {t("Tests.detailValidatesPost")}
                                      {cov.names.length > 0 && (
                                        <span style={{ color: "#64748b" }}>{t("Tests.detailExamples", { names: cov.names.slice(0, 5).join(", ") })}{cov.count > 5 ? t("Tests.detailMore", { count: cov.count - 5 }) : ""}</span>
                                      )}.
                                    </p>
                                  );
                                })()}
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.colType")}</p>
                                <p className="ts-detail-val">{test.type || "—"}</p>
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.detailAssignedTo")}</p>
                                <p className="ts-detail-val">{test.assignedTo || t("Tests.unassigned")}</p>
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.colDueDate")}</p>
                                <p className="ts-detail-val">
                                  {test.dueDate ? dayjs(test.dueDate).format("MMM D, YYYY") : t("Tests.notSet")}
                                </p>
                              </div>
                              <div className="ts-detail-block">
                                <p className="ts-detail-label">{t("Tests.detailJiraIssue")}</p>
                                <p className="ts-detail-val">
                                  {test.jiraId ? (
                                    <a
                                      href={`https://markups.atlassian.net/browse/${test.jiraId}`}
                                      target="_blank" rel="noreferrer"
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                                        padding: '2px 8px', borderRadius: '4px', fontWeight: 600,
                                        background: '#e0f2fe', color: '#0369a1', textDecoration: 'none',
                                        border: '1px solid #bae6fd', fontSize: '12px',
                                      }}
                                    >
                                      <i className="fab fa-jira" />
                                      {test.jiraId}
                                    </a>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>{t("Tests.notSynced")}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {/system description/i.test(test.testName || "") && (
                              <div
                                style={{
                                  margin: "0 0 12px", padding: "12px 14px", borderRadius: "8px",
                                  background: "#eef2ff", border: "1px solid #c7d2fe",
                                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
                                }}
                              >
                                <span style={{ color: "#3730a3", fontSize: "13px", lineHeight: 1.4 }}>
                                  <i className="fas fa-file-alt" style={{ marginRight: "6px" }} />
                                  {t("Tests.sysDescCallout")}
                                </span>
                                <button
                                  onClick={() => navigate(`/dashboard/tests/${test.testId}`)}
                                  style={{
                                    flexShrink: 0, background: "#4f46e5", color: "#fff", border: "none",
                                    padding: "8px 16px", borderRadius: "6px", fontWeight: 600, fontSize: "13px",
                                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "7px",
                                  }}
                                >
                                  <i className="fas fa-pen" /> {t("Tests.editSystemDescription")}
                                </button>
                              </div>
                            )}
                            <div className="ts-detail-footer">
                              <button
                                className="ts-detail-link"
                                onClick={() => navigate(`/dashboard/tests/${test.testId}`)}
                              >
                                <i className="fas fa-history" /> {t("Tests.viewHistory")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="ts-pagination">
          <div className="ts-page-info">
            <select
              className="ts-per-page"
              value={testsPerPage}
              onChange={e => { setTestsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[5, 10, 15, 20].map(n => <option key={n} value={n}>{t("Tests.perPage", { count: n })}</option>)}
            </select>
            {totalElements > 0 && (
              <span className="ts-total-label">{t("Tests.totalCount", { count: totalElements })}</span>
            )}
          </div>
          <div className="ts-page-controls">
            <button className="ts-page-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
              <i className="fas fa-chevron-left" />
            </button>
            <span className="ts-page-num">{t("Tests.pageOf", { current: currentPage, total: totalPages })}</span>
            <button className="ts-page-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      {/* Owner dropdown rendered in a portal so overflow clipping never buries it */}
      {openDropdown !== null && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="ts-owner-dropdown"
          style={{
            position: "fixed",
            top:    dropdownPos.top,
            bottom: dropdownPos.bottom,
            left:   dropdownPos.left,
            zIndex: 9999,
          }}
        >
          <div className="ts-owner-option ts-owner-unassign"
            onClick={() => assignOwner(testData.find(t => t.id === openDropdown), "", null)}>
            {t("Tests.unassignedOption")}
          </div>
          {availableUsers.map(user => (
            <div
              key={user.id}
              className="ts-owner-option"
              onClick={() => assignOwner(testData.find(t => t.id === openDropdown), user.userName, user.id)}
            >
              {user.userName}
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Toast */}
      {toast && (
        <div className={`ts-toast ${toast.ok ? "ts-toast-ok" : "ts-toast-err"}`}>
          <i className={`fas fa-${toast.ok ? "check-circle" : "exclamation-circle"}`} />
          {toast.msg}
        </div>
      )}

      {urlEntry && (
        <div
          onClick={() => !urlEntry.busy && setUrlEntry(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex",
                   alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div onClick={e => e.stopPropagation()}
               style={{ background: "#fff", borderRadius: 10, padding: "22px 24px", width: 460, maxWidth: "92vw",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 16 }}>{t("Tests.modalTitle")}</h3>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7280" }}>
              {t("Tests.modalBodyPre")} <strong>{urlEntry.test.testName}</strong>. {t("Tests.modalBodyPost")}
            </p>
            <input
              type="url"
              autoFocus
              placeholder="https://yourcompany.com/privacy-policy"
              value={urlEntry.value}
              disabled={urlEntry.busy}
              onChange={e => setUrlEntry(u => ({ ...u, value: e.target.value }))}
              onKeyDown={e => { if (e.key === "Enter") submitLegalDocUrl(); }}
              style={{ width: "100%", padding: "9px 11px", border: "1px solid #d1d5db", borderRadius: 7,
                       fontSize: 14, boxSizing: "border-box" }}
            />
            {urlEntry.error && (
              <div style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{urlEntry.error}</div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button onClick={() => setUrlEntry(null)} disabled={urlEntry.busy}
                      style={{ padding: "8px 14px", border: "1px solid #d1d5db", borderRadius: 7,
                               background: "#fff", cursor: "pointer" }}>
                {t("Tests.cancel")}
              </button>
              <button onClick={submitLegalDocUrl} disabled={urlEntry.busy || !urlEntry.value.trim()}
                      style={{ padding: "8px 16px", border: "none", borderRadius: 7, background: "#4338ca",
                               color: "#fff", cursor: "pointer", opacity: urlEntry.busy ? 0.7 : 1 }}>
                {urlEntry.busy ? t("Tests.validating") : t("Tests.validateSave")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tests;
